import { z } from 'zod';

/**
 * `GET /user`, as far as the frontend reads it: `user.controller.js#getUser` over the profile
 * `attachCurrentUser` loads (`userProfileSelect`). Each object passes through the keys it does
 * not name: the check is on what a screen or a gate reads, not on the whole row.
 *
 * Checked by `userApi.getUser` through `checkResponse` (`./responseContract.ts`). The closed sets
 * are the API's Prisma enums, held equal to the client's constants by `schemas.test.ts`.
 */

/** `TeamType`. */
export const teamTypeSchema = z.enum(['audit_firm', 'fiduciary']);

/** `SignatoryStatus`: where the caller's identity verification for signing stands. */
export const signatoryStatusSchema = z.enum(['pending', 'verified', 'rejected']);

/**
 * The caller's platform team, from their home `PlatformTeamMember` (or the first): the role's
 * name and the team's type. ABSENT for a user on no platform team, every client among them.
 * Read as the team role by the company settings' admin gates and as the team type by *Create*
 * on the audits list.
 */
export const platformRoleSchema = z
  .object({
    role: z.string(),
    team: teamTypeSchema,
  })
  .passthrough();

export const currentUserSchema = z
  .object({
    id: z.number(),
    /** The account settings and the company team find the caller's own row by it. */
    email: z.string(),
    /** `'user'` or `'super-admin'` (`attachCurrentUser` refuses a `'guest'`): the admin gate. */
    role: z.string(),
    platformRole: platformRoleSchema.optional(),
    firstName: z.string(),
    lastName: z.string(),
    title: z.string(),
    phone: z.string(),
    country: z.string(),
    language: z.string(),
    /** `''` without a logo; presigned when it has one, and absent when presigning failed. */
    logo: z.string().optional(),
    lastActive: z.string().nullable(),
    signatoryStatus: signatoryStatusSchema.nullable(),
    /** Read only for its code; `null` until identity verification attaches one. */
    voucher: z.object({ code: z.string() }).passthrough().nullable(),
    showNotifications: z.boolean(),
    statusUpdateNotification: z.boolean(),
    documentUploadNotification: z.boolean(),
    commentAddedNotification: z.boolean(),
    commentMentionNotification: z.boolean(),
    requestCreatedNotification: z.boolean(),
  })
  .passthrough();

export const userResponseSchema = z
  .object({
    user: currentUserSchema,
    /** Opens the identity-verification tab of the account settings. */
    hasSignatoryRights: z.boolean(),
  })
  .passthrough();
